// ============================================================
// lib/browser.js — what the app keeps and does in this browser: saved videos and fonts, backups, uploads, logos, web addresses, loading app parts, error reports.
//
// In this file, in order:
//   Videos and fonts kept in this browser
//   Backups
//   Your fonts
//   Uploaded pictures
//   SVG logos
//   The default logo
//   Web addresses
//   Loading parts of the app
//   Error reports
//   Sharing
//   Downloading files
// ============================================================

import { lazy } from "react";
import { MONITORING } from "../account/AccountContext";

/* ============================================================
   Videos and fonts kept in this browser
   ============================================================ */

/** Text made safe to put inside HTML or SVG (& < > " become entities). */
export const escapeHtml = (text) => String(text == null ? "" : text).replace(/&/g, "&amp;").replace(/</g, "&lt;")
  .replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Reads something saved in this browser (JSON). null if nothing's there, or
 * storage isn't available (private browsing). { session: true } reads the
 * tab's own storage, which is cleared when the tab closes.
 */
export function readSaved(key, { session = false } = {}) {
  let raw = null;
  try { raw = (session ? sessionStorage : localStorage).getItem(key); } catch { return null; }
  if (raw == null) return null;
  try { return JSON.parse(raw); } catch { return raw; } // older values were saved as plain text
}
/** Saves something in this browser (as JSON). Returns false if it couldn't (full, or private browsing). */
export function writeSaved(key, value, { session = false } = {}) {
  const store = () => (session ? sessionStorage : localStorage);
  try {
    if (value === undefined || value === null) store().removeItem(key);
    else store().setItem(key, JSON.stringify(value));
    return true;
  } catch { return false; }
}

// Where the workspace is saved in this browser: four keys, so a large logo or
// many events can never block your edits from saving. A backup file holds all
// four. Add new keys here.
export const STORAGE_KEY = "gov-social-kit:v2";          // your designs, photos, captions, selected design
export const BRAND_KEY = "gov-social-kit-brand:v1";      // colours, logo and settings
export const EVENTS_KEY = "gov-social-kit-events:v1";    // your events, holiday closures, posted steps
export const LOG_KEY = "gov-social-kit-log:v1";          // accessibility check log (one line per post)
export const SAVED_KEYS = [STORAGE_KEY, BRAND_KEY, EVENTS_KEY, LOG_KEY];

// Videos and font files are far too large for localStorage, so they live in
// IndexedDB (the browser's store for big files, on this device only). A
// template's background keeps just the video's id, plus a poster frame (a
// still image) that thumbnails, PNG downloads and contrast checks use.

const DB = "gov-social-kit-media";
const STORES = ["videos", "fonts"]; // version 2 added "fonts"

function open() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("This browser can't store files."));
    const req = indexedDB.open(DB, 2);
    req.onupgradeneeded = () => STORES.forEach((s) => { if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s); });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function runIn(store, mode, fn) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = fn(tx.objectStore(store));
    tx.oncomplete = () => { db.close(); resolve(req && req.result); };
    tx.onerror = () => { db.close(); reject(tx.error || new Error("Couldn't save the file: storage may be full.")); };
  });
}
const run = (mode, fn) => runIn("videos", mode, fn);

/** Font files you upload: { family, weight, style, file (name), data (Blob) } by id. */
export const fontStore = {
  all: () => runIn("fonts", "readonly", (s) => s.getAll()).then((list) => list || []),
  put: (record) => runIn("fonts", "readwrite", (s) => s.put(record, record.id)),
  remove: (id) => runIn("fonts", "readwrite", (s) => s.delete(id)),
};

export async function saveVideo(blob) {
  const id = `vid-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  await run("readwrite", (s) => s.put(blob, id));
  return id;
}
export const loadVideo = (id) => run("readonly", (s) => s.get(id));
export const videoIds = () => run("readonly", (s) => s.getAllKeys()).then((ids) => ids || []);
export const putVideo = (id, blob) => run("readwrite", (s) => s.put(blob, id));
export const deleteVideo = (id) => run("readwrite", (s) => s.delete(id)).catch(() => {});

// One object URL per stored video, reused everywhere it plays.
const urls = new Map();
export async function videoUrl(id) {
  if (!id) return null;
  if (urls.has(id)) return urls.get(id);
  const blob = await loadVideo(id);
  if (!blob) return null;
  const url = URL.createObjectURL(blob);
  urls.set(id, url);
  return url;
}

/* ============================================================
   Backups
   ============================================================ */

// Backup and restore: everything this device saved for the workspace
// (designs, brand, calendar, check log, and optionally videos) in one file,
// so it can be kept safe, moved to another computer or handed to a colleague.
// The passcode is not included: whoever restores it signs in with their own.


const APP = "gov-social-kit-backup";
const VERSION = 1;

/** A mailto: link that opens the person's own mail app with a message ready (nothing is sent from here). */
export const mailto = (to, subject, body) => `mailto:${to || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

/** "1 result", "3 results": a count and a word, made plural when it needs to be. */
export const plural = (n, word, many = word + "s") => `${n} ${n === 1 ? word : many}`;

/** A file or blob as a data: URL (text that can be saved or put inside an SVG). */
export const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

/** The backup as a JSON string. */
export async function makeBackup({ workspace, includeVideos }) {
  const saved = {};
  SAVED_KEYS.forEach((key) => { const v = localStorage.getItem(key); if (v != null) saved[key] = v; });
  const videos = {};
  if (includeVideos) {
    for (const id of await videoIds().catch(() => [])) {
      const blob = await loadVideo(id);
      if (blob) videos[id] = await blobToDataUrl(blob);
    }
  }
  // Your fonts are small, so they're always included.
  const fonts = [];
  for (const f of await fontStore.all().catch(() => [])) {
    fonts.push({ id: f.id, family: f.family, weight: f.weight, style: f.style, file: f.file, data: await blobToDataUrl(f.data) });
  }
  return JSON.stringify({ app: APP, version: VERSION, workspace, made: new Date().toISOString(), saved, videos, fonts });
}

/**
 * Reads a backup file and returns what's in it, without changing anything,
 * so the person can confirm first. Throws a readable error for a wrong file.
 */
export async function readBackup(file) {
  let data;
  try { data = JSON.parse(await file.text()); } catch { throw new Error("That file isn't a Gov Social Kit backup."); }
  if (!data || data.app !== APP || typeof data.saved !== "object") throw new Error("That file isn't a Gov Social Kit backup.");
  if (data.version > VERSION) throw new Error("This backup was made by a newer version. Reload the page and try again.");
  // Only the keys this app uses, and only if they're valid saved data.
  const saved = {};
  SAVED_KEYS.forEach((key) => {
    const v = data.saved[key];
    if (typeof v !== "string") return;
    try { JSON.parse(v); saved[key] = v; } catch { /* skip a damaged part */ }
  });
  const videos = Object.entries(data.videos || {}).filter(([id, url]) => /^vid-[a-z0-9]+$/.test(id) && /^data:video\//.test(url));
  const fonts = (Array.isArray(data.fonts) ? data.fonts : []).filter((f) => f && /^font-[a-z0-9]+$/.test(f.id)
    && /^[A-Za-z0-9 -]{1,48}$/.test(f.family) && /^\d{3}( \d{3})?$/.test(String(f.weight)) && ["normal", "italic"].includes(f.style)
    && /^data:(font\/|application\/(font|x-font|octet-stream|vnd\.ms-opentype))/.test(f.data));
  return { workspace: String(data.workspace || ""), made: data.made, saved, videos, fonts };
}

// A data: URL back to a file (fetch() can't, under the site's security policy).
function fromDataUrl(url) {
  const [head, body] = url.split(",");
  const bytes = Uint8Array.from(atob(body), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: head.slice(5).split(";")[0] });
}

/** Replaces this device's saved workspace with the backup. Reload afterwards. */
export async function restoreBackup({ saved, videos, fonts = [] }) {
  for (const [id, url] of videos) await putVideo(id, fromDataUrl(url));
  for (const f of fonts) await fontStore.put({ id: f.id, family: f.family, weight: String(f.weight), style: f.style, file: String(f.file || "").slice(0, 80), data: fromDataUrl(f.data) });
  SAVED_KEYS.forEach((key) => { if (saved[key] == null) localStorage.removeItem(key); else localStorage.setItem(key, saved[key]); });
}

/* ============================================================
   Your fonts
   ============================================================ */

// Your own fonts (for example ones downloaded from Google Fonts),
// used in designs. No connection to Google or anyone else: you upload the
// font files, they're kept in this browser (IndexedDB), and:
//   • the preview uses them through the browser's FontFace API;
//   • downloads (PNG, video) carry them inside the picture's SVG, because an
//     SVG drawn as an image can't see the page's fonts.
//
// Google Fonts downloads are a .zip of .ttf files: unzip it and upload the
// weights you want (Regular and Bold cover most designs), or the one
// "VariableFont" file, which has every weight.


export const BUILT_IN_FONTS = [
  { family: "Arial", css: "Arial, sans-serif" },
  { family: "Georgia", css: "Georgia, serif" },
];
const FILE_TYPES = /\.(ttf|otf|woff2?)$/i;
const MAX_BYTES = 3 * 1024 * 1024;
const safeFamily = (name) => String(name || "").replace(/[^A-Za-z0-9 -]/g, "").replace(/\s+/g, " ").trim().slice(0, 48);

/** The CSS font-family for a family name, with a fallback. */
export const cssFamily = (family) => {
  const built = BUILT_IN_FONTS.find((f) => f.family === family);
  return built ? built.css : `'${safeFamily(family)}', Arial, sans-serif`;
};
/** The family name inside a CSS font-family ("'Roboto Slab', Arial" -> "Roboto Slab"). */
export const familyOf = (css) => String(css || "Arial").split(",")[0].replace(/['"]/g, "").trim();

/* ------------------------------------------------------------ reading a font file */

// A .ttf/.otf file's own name, weight and style (its "name", "OS/2" and
// "fvar" tables), so "PTSans-Bold.ttf" is "PT Sans", bold. WOFF files are
// compressed, so their names come from the file name instead.
function readSfnt(buffer) {
  const v = new DataView(buffer);
  const tag = v.getUint32(0);
  if (tag !== 0x00010000 && tag !== 0x4f54544f && tag !== 0x74727565) return null; // TrueType, OTTO, true
  const tables = {};
  for (let i = 0, n = v.getUint16(4); i < n; i += 1) {
    const at = 12 + i * 16;
    const name = String.fromCharCode(v.getUint8(at), v.getUint8(at + 1), v.getUint8(at + 2), v.getUint8(at + 3));
    tables[name] = { offset: v.getUint32(at + 8), length: v.getUint32(at + 12) };
  }
  const out = {};
  if (tables.name) {
    const base = tables.name.offset;
    const count = v.getUint16(base + 2);
    const strings = base + v.getUint16(base + 4);
    const names = {};
    for (let i = 0; i < count; i += 1) {
      const r = base + 6 + i * 12;
      const platform = v.getUint16(r), encoding = v.getUint16(r + 2), id = v.getUint16(r + 6);
      const length = v.getUint16(r + 8), offset = strings + v.getUint16(r + 10);
      if (names[id] || ![1, 2, 16, 17].includes(id)) continue;
      let text = "";
      if (platform === 3 && (encoding === 1 || encoding === 10)) {
        for (let k = 0; k < length; k += 2) text += String.fromCharCode(v.getUint16(offset + k));
      } else if (platform === 1 && encoding === 0) {
        for (let k = 0; k < length; k += 1) text += String.fromCharCode(v.getUint8(offset + k));
      } else continue;
      names[id] = text;
    }
    out.family = names[16] || names[1];
    out.subfamily = names[17] || names[2];
  }
  if (tables["OS/2"]) {
    out.weight = v.getUint16(tables["OS/2"].offset + 4);
    out.italic = Boolean(v.getUint16(tables["OS/2"].offset + 62) & 1);
  }
  if (tables.fvar) {
    const base = tables.fvar.offset;
    const axes = base + v.getUint16(base + 4);
    for (let i = 0, n = v.getUint16(base + 8), size = v.getUint16(base + 10); i < n; i += 1) {
      const a = axes + i * size;
      if (v.getUint32(a) === 0x77676874) { // "wght": a variable font with a range of weights
        out.weight = `${Math.round(v.getInt32(a + 4) / 65536)} ${Math.round(v.getInt32(a + 12) / 65536)}`;
      }
    }
  }
  return out;
}

const WEIGHTS = { thin: 100, hairline: 100, extralight: 200, ultralight: 200, light: 300, regular: 400, normal: 400, book: 400,
  medium: 500, semibold: 600, demibold: 600, bold: 700, extrabold: 800, ultrabold: 800, black: 900, heavy: 900 };

/** Family, weight and style for a font file, from inside the file, else its name. */
export function describeFont(fileName, buffer) {
  let inside = {};
  try { inside = (buffer && readSfnt(buffer)) || {}; } catch { /* unusual file: fall back to its name */ }
  // "RobotoSlab-Bold.ttf", "Inter-Italic-VariableFont_opsz,wght.ttf"…
  const variable = /variablefont/i.test(fileName);
  const base = fileName.replace(FILE_TYPES, "").replace(/[-_]?VariableFont.*$/i, "");
  const m = base.match(/^(.*?)[-_]([A-Za-z]+)$/);
  const styleWord = m ? m[2].toLowerCase().replace("italic", "") : "";
  const knownStyle = m && (styleWord === "" || WEIGHTS[styleWord]);
  const rawFamily = knownStyle ? m[1] : base;
  const family = safeFamily(inside.family || rawFamily.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " "));
  const weight = inside.weight || (variable ? "100 900" : WEIGHTS[styleWord] || 400);
  const italic = inside.italic ?? /italic/i.test(`${m ? m[2] : ""} ${inside.subfamily || ""}`);
  return { family, weight: String(weight), style: italic ? "italic" : "normal" };
}

/* ------------------------------------------------------------ using fonts */

const loaded = new Map(); // id -> { family, weight, style, dataUrl }


// Tells the design renderer to measure text again with the new font.
const announce = () => { try { document.fonts.dispatchEvent(new Event("loadingdone")); } catch { /* old browser */ } };

async function register(record) {
  const buffer = await record.data.arrayBuffer();
  const face = new FontFace(record.family, buffer, { weight: record.weight, style: record.style });
  await face.load(); // rejects if the file isn't a usable font
  document.fonts.add(face);
  loaded.set(record.id, { family: record.family, weight: record.weight, style: record.style, dataUrl: await blobToDataUrl(record.data) });
}

/** Loads every saved font (once, when the workspace opens). Returns their descriptions. */
export async function loadSavedFonts() {
  const records = await fontStore.all().catch(() => []);
  const ok = [];
  for (const r of records) {
    try { await register(r); ok.push({ id: r.id, family: r.family, weight: r.weight, style: r.style, file: r.file }); } catch { /* skip a damaged file */ }
  }
  announce();
  return ok;
}

/** Families from designs.json → "fonts" (for the font pickers), once loaded. */
export const DESIGN_FONTS = [];

/**
 * Loads the fonts designs.json lists (files next to it), the same way as
 * your own: the preview uses them, and downloads carry them inside the picture.
 * A font that can't be loaded is skipped (its text shows in the fallback font).
 */
export async function loadDesignFonts(fonts = []) {
  if (typeof FontFace === "undefined" || typeof document === "undefined" || !fonts.length) return;
  await Promise.all(fonts.map(async (f) => {
    const id = `designs:${f.family}:${f.weight}:${f.style}`;
    if (loaded.has(id)) return;
    try {
      const res = await fetch(f.src);
      if (!res.ok) throw new Error(String(res.status));
      await register({ id, family: f.family, weight: f.weight, style: f.style, data: await res.blob() });
      if (!DESIGN_FONTS.includes(f.family)) DESIGN_FONTS.push(f.family);
    } catch (err) {
      console.warn(`designs.json: the font "${f.family}" couldn't be loaded (${err.message}).`);
    }
  }));
  announce();
}

/**
 * Adds font files. Each is checked (a real font, under 3 MB) before it's kept.
 * @returns {Promise<{ added: Array<{id, family, weight, style, file}>, errors: string[] }>}
 */
export async function addFontFiles(files) {
  const added = [];
  const errors = [];
  for (const file of files) {
    if (!FILE_TYPES.test(file.name)) { errors.push(`${file.name}: use a .ttf, .otf, .woff or .woff2 file.`); continue; }
    if (file.size > MAX_BYTES) { errors.push(`${file.name}: over 3 MB. Use a single weight rather than a whole family.`); continue; }
    try {
      const buffer = await file.arrayBuffer();
      const record = { id: `font-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, file: file.name.slice(0, 80),
        ...describeFont(file.name, buffer), data: new Blob([buffer], { type: file.type || "font/ttf" }) };
      if (!record.family) throw new Error("no name");
      await register(record);
      await fontStore.put(record);
      added.push({ id: record.id, family: record.family, weight: record.weight, style: record.style, file: record.file });
    } catch {
      errors.push(`${file.name}: couldn't be read as a font.`);
    }
  }
  announce();
  return { added, errors };
}

export async function removeFont(id) {
  loaded.delete(id);
  await fontStore.remove(id).catch(() => {});
}

/** @font-face rules (with the font files inside) for the families a template uses. */
export function fontFaceCss(template) {
  const used = new Set(template.layers.filter((l) => l.type === "text").map((l) => familyOf(l.fontFamily)));
  return [...loaded.values()].filter((f) => used.has(f.family))
    .map((f) => `@font-face{font-family:'${f.family}';src:url(${f.dataUrl});font-weight:${f.weight};font-style:${f.style}}`)
    .join("");
}

/* ============================================================
   Uploaded pictures
   ============================================================ */

// Turns an uploaded image file into a base64 data URL automatically,
// downscaled so it fits comfortably in browser storage, and measures its
// average colour (the "tone") so contrast checks know what's behind the text.

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("That image couldn't be opened. Try a PNG, JPG or WebP."));
    img.src = src;
  });
}

const readAsDataUrl = (file) => blobToDataUrl(file).catch(() => { throw new Error("That file couldn't be read."); });

// Average colour of an image (or its central band), as #rrggbb.
function averageTone(img, band = { top: 0, bottom: 1 }) {
  const w = 48;
  const h = Math.max(1, Math.round((img.naturalHeight / img.naturalWidth) * w));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  const y0 = Math.floor(band.top * h);
  const { data } = ctx.getImageData(0, y0, w, Math.max(1, Math.ceil(band.bottom * h) - y0));
  let r = 0, g = 0, b = 0;
  const n = data.length / 4;
  for (let i = 0; i < data.length; i += 4) { r += data[i]; g += data[i + 1]; b += data[i + 2]; }
  return "#" + [r, g, b].map((c) => Math.round(c / n).toString(16).padStart(2, "0")).join("");
}

/**
 * @param {File|string} source - an uploaded File, or an existing data URL
 * @param {{maxSize?: number, keepTransparency?: boolean}} opts
 * @returns {Promise<{dataUrl: string, tone: string, width: number, height: number}>}
 */
export async function imageToBase64(source, { maxSize = 1920, keepTransparency = false } = {}) {
  if (typeof source !== "string" && !(source.type || "").startsWith("image/")) {
    throw new Error("Choose an image file (PNG, JPG or WebP).");
  }
  const original = typeof source === "string" ? source : await readAsDataUrl(source);
  const img = await loadImage(original);
  const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  // Photos become JPEG (much smaller); logos stay PNG to keep transparency.
  const dataUrl = keepTransparency ? canvas.toDataURL("image/png") : canvas.toDataURL("image/jpeg", 0.86);

  return { dataUrl, tone: averageTone(img, { top: 0.2, bottom: 0.85 }), width, height };
}

/* ============================================================
   SVG logos
   ============================================================ */

// A county's own SVG logo, kept as a vector (sharp at every size,
// 4K and print included), with its own colours.
//
// An SVG file can carry code, so it's cleaned before it's kept: scripts,
// embedded web pages, animations, event handlers and links to anything
// outside the file are removed. Only the drawing stays. It's then used as an
// image (never put into the page as markup), which can't run code anyway.

const MAX_SVG_BYTES = 400 * 1024;
const DROP = ["script", "foreignObject", "iframe", "object", "embed", "audio", "video", "canvas",
  "animate", "animateMotion", "animateTransform", "set", "discard", "handler", "listener"];
const SAFE_HREF = /^(#|data:image\/(png|jpe?g|gif|webp);base64,)/i;
// url(...) in styles: only references inside the file (#gradient) stay.
const outsideUrls = (css) => String(css).replace(/@import[^;]*;?/gi, "").replace(/url\(\s*(['"]?)(?!#)[^)]*\1\s*\)/gi, "none");

/**
 * Cleans an SVG logo file's text and returns it as a data URL.
 * @throws Error with a readable message if it isn't a usable SVG
 */
export function cleanSvgLogo(text) {
  if (text.length > MAX_SVG_BYTES) throw new Error("That SVG is over 400 KB. Export it again without embedded photos, or use a PNG.");
  const doc = new DOMParser().parseFromString(text, "image/svg+xml");
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== "svg" || doc.getElementsByTagName("parsererror").length) {
    throw new Error("That file isn't a readable SVG.");
  }
  DROP.forEach((tag) => [...svg.getElementsByTagName(tag)].forEach((n) => n.remove()));
  [svg, ...svg.getElementsByTagName("*")].forEach((el) => {
    [...el.attributes].forEach((a) => {
      const name = a.name.toLowerCase();
      if (name.startsWith("on")) el.removeAttribute(a.name);
      else if (name === "href" || name === "xlink:href") { if (!SAFE_HREF.test(a.value.trim())) el.removeAttribute(a.name); }
      else if (name === "style" || /url\(/i.test(a.value)) el.setAttribute(a.name, outsideUrls(a.value));
    });
    if (el.nodeName.toLowerCase() === "style") el.textContent = outsideUrls(el.textContent);
  });
  // A size and viewBox, so it scales cleanly (512 on its long side).
  const num = (v) => parseFloat(String(v || "").replace(/px$/, ""));
  let box = (svg.getAttribute("viewBox") || "").trim().split(/[\s,]+/).map(Number);
  if (box.length !== 4 || box.some((n) => !Number.isFinite(n)) || box[2] <= 0 || box[3] <= 0) {
    const w = num(svg.getAttribute("width")), h = num(svg.getAttribute("height"));
    if (!(w > 0 && h > 0)) throw new Error("That SVG has no size. Export it again with a viewBox, or use a PNG.");
    box = [0, 0, w, h];
    svg.setAttribute("viewBox", box.join(" "));
  }
  const k = 512 / Math.max(box[2], box[3]);
  svg.setAttribute("width", String(Math.round(box[2] * k)));
  svg.setAttribute("height", String(Math.round(box[3] * k)));
  if (!svg.getAttribute("xmlns")) svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  if (!svg.querySelector("path, circle, rect, polygon, polyline, ellipse, line, text, image, use")) {
    throw new Error("That SVG has nothing to draw.");
  }
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(svg));
}

/** A PNG copy of an SVG logo (for emails: Gmail and Outlook don't show SVG). */
export function svgToPng(dataUrl, size = 512) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const k = size / Math.max(img.naturalWidth || size, img.naturalHeight || size);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round((img.naturalWidth || size) * k);
      canvas.height = Math.round((img.naturalHeight || size) * k);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("That SVG couldn't be drawn."));
    img.src = dataUrl;
  });
}

export const isSvgUrl = (url) => /^data:image\/svg\+xml/i.test(String(url || ""));

/* ============================================================
   The default logo
   ============================================================ */

// A simple default logo until the county uploads its own: the workspace's
// initials ("Adams County" -> "AC") on the brand colour. It's a vector (SVG),
// so it stays sharp at every size; emails get a PNG copy (defaultLogoPng).

const cache = new Map();
const SKIP = new Set(["county", "city", "town", "township", "village", "of", "the", "and", "district"]);

function initialsOf(name) {
  const words = String(name || "").split(/\s+/).filter(Boolean);
  const main = words.filter((w) => !SKIP.has(w.toLowerCase()));
  // "Adams County" -> A + C: first real word, then the jurisdiction word if there's only one real word.
  const picked = main.length >= 2 ? main.slice(0, 2) : [main[0] || words[0], words.find((w) => SKIP.has(w.toLowerCase()) && w.length > 3)].filter(Boolean);
  return picked.map((w) => w[0].toUpperCase()).join("").slice(0, 3) || "•";
}


/** The default logo as SVG markup: a vector, sharp at any size (4K, print). */
export function defaultLogoSvg(name, color = "#0b1f3a") {
  const initials = initialsOf(name);
  const size = initials.length > 2 ? 156 : 192;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">`
    + `<circle cx="256" cy="256" r="256" fill="${escapeHtml(color)}"/>`
    + `<circle cx="256" cy="256" r="220" fill="none" stroke="#ffffff" stroke-opacity="0.85" stroke-width="12"/>`
    + `<text x="256" y="268" text-anchor="middle" dominant-baseline="middle" fill="#ffffff" font-family="Georgia, 'Times New Roman', serif" font-weight="800" font-size="${size}">${escapeHtml(initials)}</text>`
    + `</svg>`;
}

/** The default logo as an SVG data URL (what designs, previews and downloads use). */
export function defaultLogo(name, color = "#0b1f3a") {
  const key = `svg|${initialsOf(name)}|${color}`;
  if (!cache.has(key)) cache.set(key, "data:image/svg+xml;charset=utf-8," + encodeURIComponent(defaultLogoSvg(name, color)));
  return cache.get(key);
}

/**
 * The default logo as a PNG data URL, for emails and signatures only: Gmail
 * and Outlook don't show SVG images.
 */
export function defaultLogoPng(name, color = "#0b1f3a") {
  const key = `${initialsOf(name)}|${color}`;
  if (cache.has(key)) return cache.get(key);
  if (typeof document === "undefined") return null;
  const size = 512; // sharp enough for print sizes too
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = color;
  ctx.beginPath(); ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.85)";
  ctx.lineWidth = 12;
  ctx.beginPath(); ctx.arc(size / 2, size / 2, size / 2 - 36, 0, Math.PI * 2); ctx.stroke();
  const initials = initialsOf(name);
  ctx.fillStyle = "#ffffff";
  ctx.font = `800 ${initials.length > 2 ? 156 : 192}px Georgia, "Times New Roman", serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(initials, size / 2, size / 2 + 12);
  const url = canvas.toDataURL("image/png");
  cache.set(key, url);
  return url;
}

/* ============================================================
   Web addresses
   ============================================================ */

// A web address, tidied: "county.gov/vote" -> "https://county.gov/vote".
// Returns "" for anything that isn't a web or mail link.
export function cleanUrl(text) {
  const t = String(text || "").trim();
  if (!t) return "";
  const withScheme = /^(https?:\/\/|mailto:|tel:)/i.test(t) ? t : `https://${t}`;
  try {
    const u = new URL(withScheme);
    if (!["http:", "https:", "mailto:", "tel:"].includes(u.protocol)) return "";
    if (/^https?:$/.test(u.protocol) && !u.hostname.includes(".")) return "";
    return withScheme.slice(0, 300);
  } catch {
    return "";
  }
}

/* ============================================================
   Loading parts of the app
   ============================================================ */

// Loading the app's pieces safely. The app is split into pieces (chunks)
// that load when first needed. After a new version is published, a tab that
// was already open asks for pieces of the OLD version, which are gone, and
// the browser reports "Loading chunk … failed". Instead of an error screen:
//   1. try again once (a brief network blip is the other common cause);
//   2. still failing: reload the page once, which fetches the new version.
//      Your work is saved on every change, so nothing is lost.
// If a reload already happened in the last minute, the error is shown
// (with a Reload button) rather than reloading in a loop.


const RELOADED_KEY = "gsk-reloaded-for-update";

export const isChunkError = (err) => Boolean(err) && (err.name === "ChunkLoadError"
  || /Loading (CSS )?chunk [\w-]+ failed|dynamically imported module|Importing a module script failed|error loading dynamically/i.test(String(err.message || err)));

function reloadOnce() {
  try {
    const last = Number(sessionStorage.getItem(RELOADED_KEY)) || 0;
    if (Date.now() - last < 60000) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch { return false; }
  window.location.reload();
  return true;
}

/** A dynamic import that survives a new version being published mid-visit. */
export async function loadChunk(loader) {
  try {
    return await loader();
  } catch (err) {
    if (!isChunkError(err)) throw err;
    await new Promise((r) => setTimeout(r, 600));
    try {
      return await loader();
    } catch (again) {
      if (isChunkError(again) && reloadOnce()) return new Promise(() => {}); // the page is reloading
      throw again;
    }
  }
}

/** React.lazy with the same protection. pick: the named export to use. */
export const lazyChunk = (loader, pick) => lazy(() => loadChunk(loader).then((m) => (pick ? { default: m[pick] } : m)));

/* ============================================================
   Error reports
   ============================================================ */

// ============================================================
// Hear about problems people hit, without collecting
// anything personal.
//
// Sent per report: kind, message, a short stack, the page PATH (never the
// query string, which can hold Stripe ids, or the #fragment, which holds
// whole designs), app version, browser, time. Email addresses and phone
// numbers are masked in messages. Repeats are counted once; at most
// MONITORING.maxPerSession reports per session.
//
// With no endpoint configured, reports are kept on this device only
// (Settings, "Problems on this device") so an administrator can copy them.
// ============================================================


const LOCAL_KEY = "gsk-problems";
const LOCAL_MAX = 20;
const RELEASE = (typeof process !== "undefined" && process.env && process.env.REACT_APP_VERSION) || "dev";
const seen = new Set();
let sent = 0;

/** Masks emails and phone numbers; drops URL queries and fragments. */
function scrub(text) {
  return String(text == null ? "" : text)
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]")
    .replace(/(\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/g, "[phone]")
    .replace(/(https?:\/\/[^\s?#"')]+)[?#][^\s"')]*/g, "$1")
    .slice(0, 2000);
}

/** Problems reported on this device (newest first). */
export const localProblems = () => { const list = readSaved(LOCAL_KEY); return Array.isArray(list) ? list : []; };
export const clearLocalProblems = () => writeSaved(LOCAL_KEY, null);
const remember = (report) => writeSaved(LOCAL_KEY, [report, ...localProblems()].slice(0, LOCAL_MAX)); // storage unavailable: nothing more to do

/**
 * Report a problem. `error` can be an Error or a message; `kind` groups it
 * (error, render, promise, csp, video-export, storage-full…).
 */
export function report(error, { kind = "error", extra } = {}) {
  const message = scrub((error && error.message) || error || "Unknown error");
  const key = `${kind}|${message}`;
  if (seen.has(key) || sent >= (MONITORING.maxPerSession || 20)) return;
  seen.add(key);
  sent += 1;
  const body = {
    kind, message,
    stack: scrub(error && error.stack).split("\n").slice(0, 12).join("\n") || undefined,
    path: typeof window !== "undefined" ? window.location.pathname : "",
    release: RELEASE,
    browser: typeof navigator !== "undefined" ? navigator.userAgent : "",
    time: new Date().toISOString(),
    extra: extra ? JSON.parse(scrub(JSON.stringify(extra))) : undefined,
  };
  remember(body);
  if (!MONITORING.endpoint) return;
  try {
    const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
    if (!(navigator.sendBeacon && navigator.sendBeacon(MONITORING.endpoint, blob))) {
      fetch(MONITORING.endpoint, { method: "POST", body: blob, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {});
    }
  } catch { /* never let reporting break the app */ }
}

/** Catch everything uncaught, plus anything the Content Security Policy blocks. */
export function initMonitoring() {
  if (typeof window === "undefined" || window.__hcMonitoring) return;
  window.__hcMonitoring = true;
  window.addEventListener("error", (e) => {
    if (e.error || e.message) report(e.error || e.message, { kind: "error" });
  });
  window.addEventListener("unhandledrejection", (e) => report(e.reason || "Unhandled promise rejection", { kind: "promise" }));
  // Only the blocked address's origin and the directive: enough to fix the policy.
  window.addEventListener("securitypolicyviolation", (e) => {
    let where = e.blockedURI;
    try { where = new URL(e.blockedURI).origin; } catch { /* "inline", "eval", "data"… */ }
    report(`CSP blocked ${where} (${e.effectiveDirective})`, { kind: "csp" });
  });
}

/* ============================================================
   Sharing
   ============================================================ */

// Native sharing (the device's share sheet: Messages, Mail, social apps),
// with copy/download fallbacks where the browser can't share.

export const canShare = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

export function canShareFiles(files) {
  try {
    return canShare() && typeof navigator.canShare === "function" && navigator.canShare({ files });
  } catch {
    return false;
  }
}

// A throwaway file used only to ask "can this browser share PNG files?"
let pngSupport = null;
export function canSharePng() {
  if (pngSupport === null) {
    try { pngSupport = canShareFiles([new File([new Uint8Array(1)], "x.png", { type: "image/png" })]); } catch { pngSupport = false; }
  }
  return pngSupport;
}

/**
 * Share a link or text. Returns "shared" | "copied" | "cancelled" | "failed".
 * Falls back to copying `url` (or `text`) when there's no share sheet.
 */
export async function shareOrCopy({ title, text, url }) {
  if (canShare()) {
    try {
      await navigator.share({ title, text, url });
      return "shared";
    } catch (err) {
      if (err && err.name === "AbortError") return "cancelled";
      // Some browsers refuse (e.g. permissions); fall through to copying.
    }
  }
  // Copied, the post's words go with the link: a pasted link can only show
  // the site's preview card (preview bots never see a design's details).
  try {
    await navigator.clipboard.writeText(text && url ? `${text}\n\n${url}` : url || text || "");
    return "copied";
  } catch {
    return "failed";
  }
}

/**
 * Share a file. Returns "shared" | "cancelled" | "blocked" | "unsupported".
 * "blocked" means the browser needs a fresh tap (Safari allows sharing only
 * right after a tap, and preparing the file took too long): tap again.
 */
export async function shareFile(file, { title, text } = {}) {
  if (!canShareFiles([file])) return "unsupported";
  try {
    await navigator.share({ files: [file], title, text });
    return "shared";
  } catch (err) {
    if (err && err.name === "AbortError") return "cancelled";
    if (err && err.name === "NotAllowedError") return "blocked";
    return "unsupported";
  }
}

/**
 * Copies formatted content (HTML), so pasting into Outlook, Gmail or Apple
 * Mail keeps the layout, with plain text for places that can't take it.
 * Falls back to copying a rendered selection where the Clipboard API can't.
 * @returns {Promise<boolean>}
 */
export async function copyRich(html, text) {
  try {
    if (typeof ClipboardItem === "function" && navigator.clipboard && navigator.clipboard.write) {
      await navigator.clipboard.write([new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      })]);
      return true;
    }
  } catch { /* try the selection route */ }
  const holder = document.createElement("div");
  holder.contentEditable = "true";
  holder.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;";
  holder.innerHTML = html;
  document.body.appendChild(holder);
  const range = document.createRange();
  range.selectNodeContents(holder);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  let ok = false;
  try { ok = document.execCommand("copy"); } catch { ok = false; }
  sel.removeAllRanges();
  holder.remove();
  return ok;
}

/* ============================================================
   Downloading files
   ============================================================ */

// Downloads a Blob (or text) as a file. The URL is revoked a tick later:
// revoking right after click() can cancel the download in some browsers.
export function downloadBlob(blobOrText, filename, type = "text/plain") {
  const blob = typeof blobOrText === "string" ? new Blob([blobOrText], { type: `${type};charset=utf-8` }) : blobOrText;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
